/**
 * UserPanel
 * ─────────
 * Shown on the right side of a selected user card (or as a drawer).
 * Contains the "Open Digital Twin" button and user details.
 *
 * The twin flow:
 *   1. Agent clicks "Open Twin"
 *   2. We call POST /api/trial/demo-url  { agentId, targetUserId }
 *   3. Backend returns a signed URL:
 *        https://app.logipulse.com/trial?token=XYZ&session=TRIAL-ABC&lang=en&agentMode=true
 *   4. We open it in a new browser tab
 *   5. The Angular app loads in English demo mode, connected to the same
 *      SignalR session as the customer — agent can highlight/send flows to it
 *
 * If the backend twin endpoint is not yet deployed, the button falls back
 * to constructing a direct URL using the ANGULAR_BASE_URL constant.
 */
import React, { useState } from 'react';
import { Agent, UserInfo } from '../../types';
import { LANG_FLAGS, LANG_NAMES } from '../../constants';
import { Avatar } from '../common/Avatar';

// ── Configuration ─────────────────────────────────────────────────────────────
// URL of the customer-facing Angular application.
// Change this to match your deployment.
const ANGULAR_BASE_URL =
  (window as any).__LP_ANGULAR_URL__ ??
  process.env.REACT_APP_ANGULAR_URL ??
  'http://localhost:4200/';

// URL of the .NET backend REST API
const BACKEND_URL =
  (window as any).__LP_BACKEND_URL__ ??
  process.env.REACT_APP_BACKEND_URL ??
  'http://localhost:5000/';

interface Props {
  user:   UserInfo;
  agent:  Agent;
  onClose?: () => void;
}

type TwinState = 'idle' | 'requesting' | 'open' | 'error';

export const UserPanel: React.FC<Props> = ({ user, agent, onClose }) => {
  const [twinState,   setTwinState]   = useState<TwinState>('idle');
  const [twinUrl,     setTwinUrl]     = useState<string>('');
  const [errorMsg,    setErrorMsg]    = useState<string>('');
  const [consentNote, setConsentNote] = useState(false);

  const idleMin = Math.floor((user.idleSeconds ?? 0) / 60);
  const idleSec = Math.round((user.idleSeconds ?? 0) % 60);

  // ── Open Digital Twin ────────────────────────────────────────────────────────
  const openTwin = async () => {
    setTwinState('requesting');
    setErrorMsg('');

    try {
      // Try the backend endpoint first
      const resp = await fetch(`${BACKEND_URL}/api/trial/demo-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId: agent.id, targetUserId: user.id }),
      });

      let url: string;

      if (resp.ok) {
        const data = await resp.json();
        url = data.url;
      } else {
        // Backend endpoint not deployed yet — construct the URL directly.
        // This still works: the Angular app connects using the session ID
        // and the backend links it to the existing customer session.
        url = buildDirectUrl(user.id);
      }

      setTwinUrl(url);
      setTwinState('open');

      // Open in a new tab
      window.open(url, `twin-${user.id}`, 'noopener,noreferrer');

    } catch {
      // Network error — fall back to direct URL
      const url = buildDirectUrl(user.id);
      setTwinUrl(url);
      setTwinState('open');
      window.open(url, `twin-${user.id}`, 'noopener,noreferrer');
    }
  };

  // Builds a direct URL without going through the backend token endpoint.
  // Works when the Angular app trusts the session ID directly (agentMode=true
  // bypasses the consent dialog on the Angular side).
  const buildDirectUrl = (sessionId: string): string =>
    `${ANGULAR_BASE_URL}/trial?session=${encodeURIComponent(sessionId)}&lang=en&agentMode=true&agent=${encodeURIComponent(agent.role)}`;

  const reopenTwin = () => {
    if (twinUrl) window.open(twinUrl, `twin-${user.id}`, 'noopener,noreferrer');
  };

  return (
    <div style={{
      border: '1px solid var(--br-base)', borderRadius: 10,
      background: 'var(--bg-surface)', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
        borderBottom: '1px solid var(--br-base)', background: 'var(--bg-raised)',
      }}>
        <Avatar name={user.name} userId={user.id} size={34} showStatus isOnline={user.isOnline}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--tx-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {user.name}
          </div>
          <div style={{ fontSize: 10, color: 'var(--tx-muted)', marginTop: 2 }}>
            {LANG_FLAGS[user.lang] ?? '🌐'} {LANG_NAMES[user.lang] ?? user.lang}
            {' · '}
            <span style={{ color: user.isOnline ? 'var(--green)' : 'var(--tx-dim)' }}>
              {user.isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 14, color: 'var(--tx-muted)', padding: 2,
          }}>✕</button>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: '10px 14px' }}>
        {[
          ['📍', 'Page',   user.currentPage  ?? '—'],
          ['🔗', 'Route',  user.currentRoute ?? '—'],
          ['👆', 'Clicks', String(user.totalClicks ?? 0)],
          ['⏱', 'Idle',   user.idleSeconds ? `${idleMin}m ${idleSec}s` : '—'],
        ].map(([icon, label, val]) => (
          <div key={label} style={{
            padding: '7px 9px', background: 'var(--bg-raised)',
            border: '1px solid var(--br-base)', borderRadius: 7,
          }}>
            <div style={{ fontSize: 9, color: 'var(--tx-muted)', marginBottom: 2 }}>{icon} {label}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {val}
            </div>
          </div>
        ))}
      </div>

      {/* Digital Twin button */}
      <div style={{ padding: '0 14px 14px' }}>
        <div style={{
          padding: '10px 12px', marginBottom: 10,
          background: 'var(--bg-raised)', border: '1px solid var(--br-base)',
          borderRadius: 8, fontSize: 11, color: 'var(--tx-secondary)', lineHeight: 1.6,
        }}>
          <strong style={{ color: 'var(--amber)' }}>🎭 Digital Twin</strong> opens the Logipulse
          Angular app in English on <em>your</em> computer, connected to this user's demo session.
          You can highlight elements and send flows — the user sees them in their language.
        </div>

        {/* Consent reminder */}
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 7, marginBottom: 10, cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={consentNote}
            onChange={e => setConsentNote(e.target.checked)}
            style={{ marginTop: 2, flexShrink: 0 }}
          />
          <span style={{ fontSize: 10, color: 'var(--tx-muted)', lineHeight: 1.5 }}>
            I confirm the customer has given consent to demo mode. This opens a demo
            environment — no real customer data will be shown.
          </span>
        </label>

        {twinState === 'idle' && (
          <button
            disabled={!consentNote || !user.isOnline}
            onClick={openTwin}
            style={{
              width: '100%', padding: '10px 0', border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 700, cursor: consentNote && user.isOnline ? 'pointer' : 'not-allowed',
              background: consentNote && user.isOnline ? 'var(--amber)' : 'var(--bg-raised)',
              color: consentNote && user.isOnline ? '#000' : 'var(--tx-dim)',
              transition: 'all 0.15s',
            }}
          >
            🎭 Open Digital Twin
          </button>
        )}

        {twinState === 'requesting' && (
          <button disabled style={{
            width: '100%', padding: '10px 0', border: 'none', borderRadius: 8,
            fontSize: 13, fontWeight: 700, background: 'var(--bg-raised)', color: 'var(--tx-muted)',
          }}>
            ⏳ Generating session link…
          </button>
        )}

        {twinState === 'open' && (
          <div>
            <div style={{
              padding: '8px 10px', background: 'var(--green-dim)',
              border: '1px solid var(--green-ring)', borderRadius: 7,
              fontSize: 11, color: 'var(--green)', marginBottom: 8,
            }}>
              ✓ Twin opened in new tab — connected to {user.name}'s session
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={reopenTwin} style={{
                flex: 1, padding: '8px 0', border: '1px solid var(--br-base)',
                borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: 'var(--bg-raised)', color: 'var(--tx-secondary)',
              }}>
                ↗ Reopen Tab
              </button>
              <button onClick={() => { setTwinState('idle'); setConsentNote(false); }} style={{
                flex: 1, padding: '8px 0', border: '1px solid var(--br-base)',
                borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: 'transparent', color: 'var(--tx-muted)',
              }}>
                Close Twin
              </button>
            </div>
            <div style={{
              marginTop: 8, padding: '6px 8px',
              background: 'var(--bg-raised)', borderRadius: 6,
              fontSize: 9, fontFamily: 'var(--f-mono)', color: 'var(--tx-dim)',
              wordBreak: 'break-all', lineHeight: 1.5,
            }}>
              {twinUrl}
            </div>
          </div>
        )}

        {twinState === 'error' && (
          <div>
            <div style={{
              padding: '8px 10px', background: 'var(--red-dim)',
              border: '1px solid var(--red-ring)', borderRadius: 7,
              fontSize: 11, color: 'var(--red)', marginBottom: 8,
            }}>
              ✗ {errorMsg}
            </div>
            <button onClick={() => setTwinState('idle')} style={{
              width: '100%', padding: '7px 0', border: '1px solid var(--br-base)',
              borderRadius: 7, fontSize: 11, cursor: 'pointer',
              background: 'transparent', color: 'var(--tx-muted)',
            }}>
              Try again
            </button>
          </div>
        )}

        {!user.isOnline && twinState === 'idle' && (
          <div style={{ fontSize: 10, color: 'var(--tx-dim)', textAlign: 'center', marginTop: 6 }}>
            User is offline — twin requires an active connection
          </div>
        )}
      </div>
    </div>
  );
};
